
import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { da } from "date-fns/locale";

type TaskImageGalleryProps = {
  taskId: string;
};

export const TaskImageGallery = ({ taskId }: TaskImageGalleryProps) => {
  const queryClient = useQueryClient();
  
  const { data: images, isLoading } = useQuery({
    queryKey: ['task-images', taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_images')
        .select('*')
        .eq('task_id', taskId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('task-images-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_images',
          filter: `task_id=eq.${taskId}`
        },
        (payload) => {
          console.log('Realtime update received:', payload);
          queryClient.invalidateQueries({ queryKey: ['task-images', taskId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);

  const getImageUrl = (filePath: string) => {
    const { data } = supabase.storage
      .from('task-images')
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-40 w-full" />
        ))}
      </div>
    );
  }

  if (!images?.length) {
    return (
      <div className="text-center text-gray-500 py-4">
        Ingen billeder uploadet endnu
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {images.map((image) => (
        <div key={image.id} className="relative group">
          <img
            src={getImageUrl(image.file_path)}
            alt={image.file_name}
            className="w-full h-40 object-cover rounded-lg"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg" />
        </div>
      ))}
    </div>
  );
};
